import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store";
import { selectProfile } from "@/store/selectors";
import { setProfile } from "@/store/slices/profileSlice";
import { useGetMyProfileApiV1MeProfileGet } from "@/api/generated/me/me";
import { useAuth } from "@/auth/useAuth";

/**
 * Tenant bootstrap (renamed from "onboarding bootstrap"):
 *
 * On every authenticated layout mount, calls `GET /api/v1/me/profile` on the
 * OTA search engine (which proxies controlplane `/users/me/full-profile`).
 *
 * Populates the profile slice so Redux becomes a cache:
 *   - agencyId      → current organization
 *   - agencyName    → business_name
 *   - activeRole    → org_type (OTA | TRAVEL_AGENCY | DMC | BAD_BANK)
 *   - onboardingCompletedAt → from CP, survives localStorage flush
 *   - profile prefs (destinations, clientele, budget, stars) when present
 *
 * Resolution rule mirrors `aponte_transfer_module` userProfileSlice.ts:50:
 *   `current_organization` is populated by the CP only when org_id is sent;
 *   on the first call we don't have it yet, so we derive the active from
 *   `memberships.find(m => m.status === "ACTIVE")` and write it back to
 *   Redux. The next layout mount will use that org_id transparently because
 *   the OTA AuthContext is built per-request from `X-Active-Role` header.
 */

interface Membership {
  org_id: string;
  org_name: string;
  org_type: string;
  role: string;
  status: string;
  workspace_id?: string | null;
}

interface OrganizationLike {
  org_id?: string;
  business_name?: string;
  org_type?: string;
  onboarding_completed_at?: string | null;
  profile?: {
    top_destinations?: string[] | null;
    clientele_mix?: Record<string, number> | null;
    budget_min?: number | null;
    budget_max?: number | null;
    preferred_star_ratings?: number[] | null;
  } | null;
}

interface MeProfilePayload {
  user?: { user_id?: string; email?: string; full_name?: string };
  current_organization?: OrganizationLike | null;
  memberships?: Membership[];
  enabled_modules?: { code: string; name: string }[];
}

function pickActiveMembership(memberships?: Membership[]): Membership | undefined {
  if (!memberships || memberships.length === 0) return undefined;
  // Show the organization the user actually belongs to — no preference by org_type.
  // A user's memberships resolve to a single org (multi-membership only spans
  // workspaces of the SAME org), so the first ACTIVE membership IS that org.
  return memberships.find((m) => m.status === "ACTIVE") ?? memberships[0];
}

export function useOnboardingBootstrap() {
  const dispatch = useAppDispatch();
  const profile = useAppSelector(selectProfile);
  const { isAuthenticated } = useAuth();
  // `hasHydrated` = abbiamo già processato la response e dispatched a Redux.
  // Serve a gatare `isBootstrapping` su DUE condizioni — non solo "query in
  // volo" ma anche "data ricevuto MA non ancora riversato in Redux", per
  // chiudere la race condition di un render in cui `meQuery.isPending=false`
  // ma `selectIsOnboarded` legge ancora l'initialState (false).
  const [hasHydrated, setHasHydrated] = useState(false);
  const meQuery = useGetMyProfileApiV1MeProfileGet({
    // `enabled` evita la query in stati transienti (es. KeycloakProvider
    // non ancora ready): senza bearer la chiamata farebbe un 401 inutile
    // che react-query memorizza per `staleTime` con `retry: 0`.
    query: { staleTime: 5 * 60_000, retry: 0, enabled: isAuthenticated },
  });

  useEffect(() => {
    const raw = meQuery.data as MeProfilePayload | undefined;
    if (!raw) return;

    const memberships = raw.memberships ?? [];
    const active = pickActiveMembership(memberships);

    // Trust `current_organization` ONLY when it is genuinely one of the user's
    // memberships. Otherwise it's a stale echo — the request carried a persisted
    // org_id from a previous user/session — and would display an org the user does
    // NOT belong to (e.g. "Xenia" for the Henoia admin). The membership is authoritative.
    const currentOrgRaw = raw.current_organization ?? undefined;
    const currentOrg =
      currentOrgRaw && memberships.some((m) => m.org_id === currentOrgRaw.org_id)
        ? currentOrgRaw
        : undefined;

    const agencyId = currentOrg?.org_id ?? active?.org_id;
    const agencyName = currentOrg?.business_name ?? active?.org_name;
    const activeRole = (currentOrg?.org_type ?? active?.org_type) as
      | typeof profile.activeRole
      | undefined;

    const update: Parameters<typeof setProfile>[0] = {};
    if (agencyId && agencyId !== profile.agencyId) update.agencyId = agencyId;
    // Keep the displayed name in sync with the resolved org: update whenever it
    // CHANGES, not only when empty — otherwise a stale persisted name (e.g. from a
    // previous login in this browser) sticks forever and the right name never shows.
    if (agencyName && agencyName !== profile.agencyName) update.agencyName = agencyName;
    if (activeRole && activeRole !== profile.activeRole) update.activeRole = activeRole;
    if (currentOrg?.onboarding_completed_at) {
      update.onboardingCompletedAt = currentOrg.onboarding_completed_at;
    }

    const p = currentOrg?.profile;
    if (p) {
      if (Array.isArray(p.top_destinations) && profile.topDestinations.length === 0) {
        update.topDestinations = p.top_destinations.map((name, i) => ({
          destination_id: `cp-${i}`,
          name,
        }));
      }
      if (p.clientele_mix && typeof p.clientele_mix === "object") {
        update.clienteleMix = {
          couples: Number(p.clientele_mix.couples ?? profile.clienteleMix.couples) || 0,
          families: Number(p.clientele_mix.families ?? profile.clienteleMix.families) || 0,
          business: Number(p.clientele_mix.business ?? profile.clienteleMix.business) || 0,
          luxury: Number(p.clientele_mix.luxury ?? profile.clienteleMix.luxury) || 0,
        };
      }
      if (typeof p.budget_min === "number") update.budgetMin = p.budget_min;
      if (typeof p.budget_max === "number") update.budgetMax = p.budget_max;
      if (Array.isArray(p.preferred_star_ratings)) {
        update.preferredStars = p.preferred_star_ratings;
      }
    }

    if (Object.keys(update).length > 0) {
      dispatch(setProfile(update));
    }
    // Marca il bootstrap come "completato" SOLO dopo il dispatch: il guard
    // in ProtectedRoute si sblocca al prossimo render quando Redux è già
    // aggiornato e `selectIsOnboarded` può essere valutato correttamente.
    setHasHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meQuery.data, dispatch]);

  // Bootstrap "in corso" finché:
  //   - non siamo autenticati (la query non parte ancora), oppure
  //   - la query è in volo (pending), oppure
  //   - data ricevuto ma non ancora dispatched in Redux (hasHydrated=false).
  // In caso di errore della query smettiamo di attendere — il guard userà
  // il valore Redux (initialState dopo purge), che porta al wizard come
  // fallback ragionevole se la chiamata bootstrap fallisce.
  return {
    isBootstrapping: isAuthenticated && !hasHydrated && !meQuery.isError,
    isBootstrapError: meQuery.isError,
  };
}
